import * as React from 'react'

/**
 * Shared markdown document page: the parser + renderer originally written for
 * the user guide (GuidePage), extracted so other in-product documents (e.g.
 * the legal pages) render through the same pipeline instead of shipping a
 * second markdown-to-HTML path. It intentionally supports only the subset of
 * markdown those documents use.
 */

type Block =
  | { type: 'h1'; text: string; id: string }
  | { type: 'h2'; text: string; id: string }
  | { type: 'h3'; text: string; id: string }
  | { type: 'p'; text: string }
  | { type: 'hr' }
  | { type: 'blockquote'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'code'; lang: string; text: string }

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .trim()
}

function parseInline(text: string, transformHref?: (href: string) => string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  const regex = /(\*\*(.+?)\*\*)|(`(.+?)`)|(\[(.+?)\]\((.+?)\))|(\*(.+?)\*)/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }

    if (match[1]) {
      nodes.push(
        <strong key={match.index} className="font-semibold text-ink">
          {match[2]}
        </strong>
      )
    } else if (match[3]) {
      nodes.push(
        <code
          key={match.index}
          className="rounded border border-line bg-bg-sunk px-1 py-0.5 font-mono text-[0.85em] text-ink"
        >
          {match[4]}
        </code>
      )
    } else if (match[5]) {
      const href = transformHref ? transformHref(match[7]) : match[7]
      const linkText = match[6]
      const isDownloadable = /\.(zip|gz|tgz|tar|rar|7z|pdf|gwbk|l5x)(\?.*)?$/i.test(href)
      nodes.push(
        <a
          key={match.index}
          href={href}
          download={isDownloadable ? '' : undefined}
          className="text-brand hover:underline"
        >
          {linkText}
        </a>
      )
    } else if (match[8]) {
      nodes.push(
        <em key={match.index} className="italic">
          {match[9]}
        </em>
      )
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }

  return nodes
}

function parseMarkdown(md: string): Block[] {
  const lines = md.split('\n')
  const blocks: Block[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (line.trim() === '') {
      i++
      continue
    }

    if (line.startsWith('# ')) {
      const text = line.slice(2).trim()
      blocks.push({ type: 'h1', text, id: slugify(text) })
      i++
      continue
    }

    if (line.startsWith('## ')) {
      const text = line.slice(3).trim()
      blocks.push({ type: 'h2', text, id: slugify(text) })
      i++
      continue
    }

    if (line.startsWith('### ')) {
      const text = line.slice(4).trim()
      blocks.push({ type: 'h3', text, id: slugify(text) })
      i++
      continue
    }

    if (line.trim() === '---') {
      blocks.push({ type: 'hr' })
      i++
      continue
    }

    if (line.trimStart().startsWith('```')) {
      const lang = line.trimStart().slice(3).trim()
      i++
      const codeLines: string[] = []
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        codeLines.push(lines[i])
        i++
      }
      if (i < lines.length) i++
      blocks.push({ type: 'code', lang, text: codeLines.join('\n') })
      continue
    }

    if (line.startsWith('> ')) {
      const bqLines: string[] = []
      while (i < lines.length && lines[i].startsWith('> ')) {
        bqLines.push(lines[i].slice(2))
        i++
      }
      blocks.push({ type: 'blockquote', text: bqLines.join(' ') })
      continue
    }

    if (line.startsWith('| ')) {
      const tableLines: string[] = []
      while (i < lines.length && lines[i].startsWith('|')) {
        tableLines.push(lines[i])
        i++
      }

      const parseRow = (row: string) =>
        row
          .split('|')
          .slice(1, -1)
          .map((cell) => cell.trim())

      const headers = parseRow(tableLines[0])
      const rows = tableLines.slice(2).map(parseRow)
      blocks.push({ type: 'table', headers, rows })
      continue
    }

    if (/^\d+\.\s/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        const itemLines = [lines[i].replace(/^\d+\.\s/, '')]
        i++
        while (
          i < lines.length &&
          lines[i].startsWith('   ') &&
          !/^\d+\.\s/.test(lines[i].trim())
        ) {
          itemLines.push(lines[i].trim())
          i++
        }
        items.push(itemLines.join(' '))
      }
      blocks.push({ type: 'ol', items })
      continue
    }

    if (line.startsWith('- ')) {
      const items: string[] = []
      while (i < lines.length && lines[i].startsWith('- ')) {
        items.push(lines[i].slice(2))
        i++
      }
      blocks.push({ type: 'ul', items })
      continue
    }

    const pLines: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('#') &&
      !lines[i].startsWith('|') &&
      !lines[i].startsWith('> ') &&
      !lines[i].startsWith('- ') &&
      !lines[i].trimStart().startsWith('```') &&
      !/^\d+\.\s/.test(lines[i]) &&
      lines[i].trim() !== '---'
    ) {
      pLines.push(lines[i])
      i++
    }
    if (pLines.length) {
      blocks.push({ type: 'p', text: pLines.join(' ') })
    } else {
      i++
    }
  }

  return blocks
}

interface MarkdownDocPageProps {
  title: string
  subtitle: string
  content: string
  /**
   * Optional rewrite applied to every link href in the markdown, e.g. mapping
   * the legal documents' relative `.md` cross-links to their in-app routes.
   */
  transformHref?: (href: string) => string
}

const MarkdownDocPage = ({ title, subtitle, content, transformHref }: MarkdownDocPageProps) => {
  const blocks = React.useMemo(() => {
    const allBlocks = parseMarkdown(content)

    // The markdown source includes its own "## Table of Contents" section (a
    // bulleted list of anchor links). The side ToC already covers this, so we
    // elide that section in render. The source files are shared with other
    // consumers (the orchestrator's read_user_guide tool, the public docs
    // site), so we filter here instead of editing the markdown.
    const tocStartIdx = allBlocks.findIndex((b) => b.type === 'h2' && b.id === 'table-of-contents')
    const tocEndIdx =
      tocStartIdx >= 0 ? allBlocks.findIndex((b, j) => j > tocStartIdx && b.type === 'h2') : -1

    return tocStartIdx >= 0 && tocEndIdx > tocStartIdx
      ? [...allBlocks.slice(0, tocStartIdx), ...allBlocks.slice(tocEndIdx)]
      : allBlocks
  }, [content])

  const toc = React.useMemo(
    () => blocks.filter((b): b is Block & { type: 'h2' } => b.type === 'h2'),
    [blocks]
  )

  const inline = (text: string) => parseInline(text, transformHref)

  return (
    <div className="mx-auto flex w-full max-w-[68rem] flex-col gap-6">
      <header>
        <h1 className="m-0 text-xl font-semibold tracking-tight text-ink">{title}</h1>
        <p className="mt-1 text-13 text-ink-3">{subtitle}</p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[12.5rem_minmax(0,1fr)]">
        <aside className="hidden self-start lg:sticky lg:top-16 lg:block">
          <div className="mb-2 text-10 font-semibold tracking-[0.12em] text-ink-3 uppercase">
            Contents
          </div>
          <nav className="flex flex-col">
            {toc.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="rounded-sm px-2 py-1 text-xs leading-relaxed text-ink-2 transition-colors hover:bg-bg-sunk hover:text-brand"
              >
                {item.text.replace(/^\d+\.\s*/, '')}
              </a>
            ))}
          </nav>
        </aside>

        <article className="max-w-[72ch] text-sm leading-6 text-ink">
          {blocks.map((block, i) => {
            switch (block.type) {
              case 'h1':
                return (
                  <h2
                    key={i}
                    id={block.id}
                    className="mt-0 mb-6 scroll-mt-20 text-xl font-semibold tracking-tight text-ink"
                  >
                    {block.text}
                  </h2>
                )
              case 'h2':
                return (
                  <h3
                    key={i}
                    id={block.id}
                    className="mt-10 mb-3 scroll-mt-20 text-base font-semibold text-ink"
                  >
                    {block.text}
                  </h3>
                )
              case 'h3':
                return (
                  <h4
                    key={i}
                    id={block.id}
                    className="mt-6 mb-2 scroll-mt-20 text-11 font-semibold tracking-[0.08em] text-ink-2 uppercase"
                  >
                    {block.text}
                  </h4>
                )
              case 'hr':
                return <hr key={i} className="my-8 border-line" />
              case 'blockquote':
                return (
                  <blockquote key={i} className="my-4 border-l-2 border-st-warn pl-3 text-ink-2">
                    {inline(block.text)}
                  </blockquote>
                )
              case 'p':
                return (
                  <p key={i} className="mb-3">
                    {inline(block.text)}
                  </p>
                )
              case 'ul':
                return (
                  <ul key={i} className="mb-3 list-disc space-y-1 pl-5 marker:text-ink-3">
                    {block.items.map((item, j) => (
                      <li key={j}>{inline(item)}</li>
                    ))}
                  </ul>
                )
              case 'ol':
                return (
                  <ol key={i} className="mb-3 list-decimal space-y-1 pl-5 marker:text-ink-3">
                    {block.items.map((item, j) => (
                      <li key={j}>{inline(item)}</li>
                    ))}
                  </ol>
                )
              case 'code':
                return (
                  <pre
                    key={i}
                    className="my-4 overflow-x-auto rounded-md border border-line bg-bg-sunk p-3 font-mono text-xs leading-relaxed text-ink"
                  >
                    {block.text}
                  </pre>
                )
              case 'table':
                return (
                  <div key={i} className="my-4 overflow-x-auto rounded-md border border-line">
                    <table className="w-full border-collapse text-xs">
                      <thead className="bg-bg-sunk">
                        <tr>
                          {block.headers.map((header, j) => (
                            <th
                              key={j}
                              className="border-b border-line px-3 py-2 text-left font-semibold text-ink"
                            >
                              {inline(header)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {block.rows.map((row, j) => (
                          <tr key={j} className="border-b border-line last:border-0">
                            {row.map((cell, k) => (
                              <td key={k} className="px-3 py-2 align-top text-ink">
                                {inline(cell)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              default:
                return null
            }
          })}
        </article>
      </div>
    </div>
  )
}

export default MarkdownDocPage
